import { useMemo } from 'react';
import type { PageTemplate as PageTemplateType, PaperSize } from '@/types/notebook';
import { TEMPLATE_CONFIG, PAPER_PIXELS } from '@/types/notebook';

type PageTemplateProps = {
  template: PageTemplateType;
  paperSize: PaperSize;
  className?: string;
};

/**
 * Renders an SVG background pattern for notebook pages.
 * Supports lines, grids, and dot patterns.
 */
export const PageTemplate = ({ template, paperSize, className }: PageTemplateProps) => {
  const config = TEMPLATE_CONFIG[template];
  const dimensions = PAPER_PIXELS[paperSize];

  const pattern = useMemo(() => {
    if (config.type === 'none') {
      return null;
    }

    const { spacing, color } = config;
    const patternId = `pattern-${template}-${paperSize}`;

    switch (config.type) {
      case 'lines':
        return (
          <defs>
            <pattern
              id={patternId}
              patternUnits="userSpaceOnUse"
              width={dimensions.width}
              height={spacing}
            >
              <line
                x1="0"
                y1={spacing}
                x2={dimensions.width}
                y2={spacing}
                stroke={color}
                strokeWidth="1"
              />
            </pattern>
          </defs>
        );

      case 'grid':
        return (
          <defs>
            <pattern
              id={patternId}
              patternUnits="userSpaceOnUse"
              width={spacing}
              height={spacing}
            >
              <path
                d={`M ${spacing} 0 L 0 0 0 ${spacing}`}
                fill="none"
                stroke={color}
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
        );

      case 'dots':
        return (
          <defs>
            <pattern
              id={patternId}
              patternUnits="userSpaceOnUse"
              width={spacing}
              height={spacing}
            >
              <circle
                cx={spacing / 2}
                cy={spacing / 2}
                r="1"
                fill={color}
              />
            </pattern>
          </defs>
        );

      default:
        return null;
    }
  }, [template, paperSize, config, dimensions]);

  const patternId = `pattern-${template}-${paperSize}`;

  return (
    <svg
      className={className}
      width={dimensions.width}
      height={dimensions.height}
      viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none',
      }}
    >
      {pattern}
      {config.type !== 'none' && (
        <rect
          width={dimensions.width}
          height={dimensions.height}
          fill={`url(#${patternId})`}
        />
      )}
    </svg>
  );
};
